<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="10">
        <v-alert v-if="!isUserAdmin" type="error" outlined class="mt-8">
          You are not allowed to access auth failure diagnostics.
        </v-alert>

        <v-card v-else class="mt-8 mb-8" :loading="loading">
          <template slot="progress">
            <v-progress-linear color="#006c27" indeterminate height="6"></v-progress-linear>
          </template>

          <v-card-title class="grey--text text--darken-2">
            Auth Failures
            <v-spacer />
            <v-btn color="#006c27" dark class="mr-2" @click="reloadAll">Reload</v-btn>
            <v-btn outlined color="#b71c1c" :disabled="loading || deleting" @click="openBulkDeleteDialog">
              Bulk Delete
            </v-btn>
          </v-card-title>

          <v-card-text>
            <div class="text-subtitle-2 mb-2">Signals (since process start)</div>
            <v-simple-table dense class="elevation-1 mb-6">
              <thead>
                <tr>
                  <th>Error Code</th>
                  <th>Count</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in signals" :key="item.errorCode">
                  <td>{{ item.errorCode }}</td>
                  <td>{{ item.count }}</td>
                  <td>{{ formatDate(item.lastSeenAt) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="text-caption grey--text mb-6">
              Process started: {{ formatDate(processStartedAt) }}
            </div>

            <v-divider class="mb-4"></v-divider>

            <div class="text-subtitle-2 mb-3">Persisted Records</div>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.fromLocal"
                  type="datetime-local"
                  label="From"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.toLocal"
                  type="datetime-local"
                  label="To"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.errorCodes"
                  :items="errorCodeOptions"
                  label="Error Codes"
                  multiple
                  chips
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="filters.requestId" label="Request ID" outlined dense></v-text-field>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field v-model="filters.oid" label="OID" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.eligibilityReasonCode"
                  label="Eligibility Reason"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.unresolvedReasonCode"
                  label="Unresolved Reason"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-btn small text color="#006c27" class="mr-1" @click="setPresetHours(1)">1h</v-btn>
                <v-btn small text color="#006c27" class="mr-1" @click="setPresetHours(24)">24h</v-btn>
                <v-btn small text color="#006c27" @click="setPresetHours(168)">7d</v-btn>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.isEligibleUser"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="Eligible User"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.isUserGroupMember"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="User Group Member"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.isAdminGroupMember"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="Admin Group Member"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.isBetaGroupMember"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="Beta Group Member"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.isInternalVisibilityMember"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="Internal Visibility"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.hasProjection"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="Has Projection"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.isUnresolved"
                  :items="triStateOptions"
                  item-text="text"
                  item-value="value"
                  label="Is Unresolved"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center justify-end">
                <v-select
                  v-model="sortBy"
                  :items="sortByOptions"
                  label="Sort By"
                  outlined
                  dense
                  class="mr-2"
                  style="max-width: 160px"
                ></v-select>
                <v-select
                  v-model="sortDirection"
                  :items="sortDirectionOptions"
                  label="Direction"
                  outlined
                  dense
                  style="max-width: 140px"
                ></v-select>
              </v-col>
            </v-row>

            <v-row class="mb-3" dense>
              <v-col cols="12" class="d-flex justify-end">
                <v-btn text color="#006c27" class="mr-2" :disabled="loading || deleting" @click="resetFilters">
                  Reset Filters
                </v-btn>
                <v-btn color="#006c27" dark :loading="loading" :disabled="deleting" @click="search">
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :items-per-page="pageSize"
              item-key="id"
              class="elevation-1"
              hide-default-footer
            >
              <template v-slot:[`item.at`]='{ item }'>
                <span class="text-caption">{{ formatDate(item.at) }}</span>
              </template>
              <template v-slot:[`item.actions`]='{ item }'>
                <v-btn icon small color="#006c27" @click="openDetail(item)">
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
                <v-btn icon small color="#b71c1c" @click="openDeleteDialog(item)">
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-row align="center" class="mt-3">
              <v-col cols="12" md="4" class="text-caption">
                Showing {{ items.length }} of {{ total }} records
              </v-col>
              <v-col cols="12" md="8" class="d-flex justify-end align-center">
                <v-btn small text color="#006c27" :disabled="page <= 1 || loading" @click="pageBack">
                  Previous
                </v-btn>
                <span class="mx-3 text-caption">Page {{ page }} / {{ maxPage }}</span>
                <v-btn small text color="#006c27" :disabled="page >= maxPage || loading" @click="pageNext">
                  Next
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="detailDialog" max-width="1100">
      <v-card>
        <v-card-title class="text--secondary">
          Auth Failure Detail
          <v-spacer></v-spacer>
          <v-btn text color="#b71c1c" :loading="deleting" @click="openDeleteDialog(detail)">
            Delete
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6"><strong>ID:</strong> {{ detail.id || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Error:</strong> {{ detail.errorCode || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>At:</strong> {{ formatDate(detail.at) }}</v-col>
            <v-col cols="12" md="6"><strong>Request ID:</strong> {{ detail.requestId || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>UPN:</strong> {{ detail.upn || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>OID:</strong> {{ detail.oid || "-" }}</v-col>
            <v-col cols="12" md="6"><strong>Status:</strong> {{ detail.status || "-" }}</v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row dense>
            <v-col cols="12" md="6">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-2">JWT Header</div>
                <v-btn x-small text color="#006c27" @click="copyText(jwtHeaderPretty)">Copy</v-btn>
              </div>
              <v-textarea :value="jwtHeaderPretty" readonly outlined auto-grow rows="10"></v-textarea>
            </v-col>
            <v-col cols="12" md="6">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-2">JWT Payload</div>
                <v-btn x-small text color="#006c27" @click="copyText(jwtPayloadPretty)">Copy</v-btn>
              </div>
              <v-textarea :value="jwtPayloadPretty" readonly outlined auto-grow rows="10"></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="retention.keepUntilLocal"
                label="Keep Until"
                type="datetime-local"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="8" class="d-flex align-center">
              <v-btn color="#006c27" dark :loading="retention.loading" :disabled="!detail.id" @click="updateRetention">
                Update Retention
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.visible" max-width="520">
      <v-card>
        <v-card-title class="text--secondary">Confirm Delete</v-card-title>
        <v-card-text>
          Delete auth failure record <strong>{{ deleteDialog.id || "-" }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="deleting" @click="deleteDialog.visible = false">Cancel</v-btn>
          <v-btn color="#b71c1c" dark :loading="deleting" @click="confirmDeleteSingle">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bulkDeleteDialog" max-width="620">
      <v-card>
        <v-card-title class="text--secondary">Confirm Bulk Delete</v-card-title>
        <v-card-text>
          Delete records matching current filters?
          <div class="text-caption grey--text mt-2">
            About {{ total }} records currently match these filters. Final deleted count can differ.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="deleting" @click="bulkDeleteDialog = false">Cancel</v-btn>
          <v-btn color="#b71c1c" dark :loading="deleting" @click="confirmBulkDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" timeout="3500">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import cass from "../api/cass";

const TRI_STATE_OPTIONS = [
  { text: "Any", value: null },
  { text: "True", value: true },
  { text: "False", value: false },
];

const KNOWN_CODES = [
  "AUTH_TOKEN_MISSING",
  "AUTH_TOKEN_INVALID",
  "AUTH_TOKEN_EXPIRED",
  "AUTH_USER_NOT_IN_REQUIRED_GROUP",
  "AUTH_REVALIDATION_DEPENDENCY_FAILED",
  "AUTH_INTERNAL_ERROR",
];

export default {
  name: "AuthFailures",
  data() {
    return {
      loading: false,
      deleting: false,
      page: 1,
      pageSize: 20,
      total: 0,
      sortBy: "at",
      sortDirection: "desc",
      headers: [
        { text: "At", value: "at" },
        { text: "Error Code", value: "errorCode" },
        { text: "Status", value: "status" },
        { text: "UPN", value: "upn" },
        { text: "Request ID", value: "requestId" },
        { text: "Eligibility", value: "eligibilityReasonCode", sortable: false },
        { text: "Unresolved", value: "unresolvedReasonCode", sortable: false },
        { text: "Path", value: "path", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      signals: [],
      processStartedAt: null,
      items: [],
      filters: {
        fromLocal: "",
        toLocal: "",
        errorCodes: [],
        requestId: "",
        oid: "",
        eligibilityReasonCode: "",
        unresolvedReasonCode: "",
        isEligibleUser: null,
        isUserGroupMember: null,
        isAdminGroupMember: null,
        isBetaGroupMember: null,
        isInternalVisibilityMember: null,
        hasProjection: null,
        isUnresolved: null,
      },
      detailDialog: false,
      detail: {},
      retention: {
        keepUntilLocal: "",
        loading: false,
      },
      deleteDialog: {
        visible: false,
        id: "",
      },
      bulkDeleteDialog: false,
      snackbar: {
        visible: false,
        color: "success",
        text: "",
      },
      triStateOptions: TRI_STATE_OPTIONS,
      errorCodeOptions: KNOWN_CODES,
      sortByOptions: ["at", "errorCode", "oid", "requestId"],
      sortDirectionOptions: ["desc", "asc"],
    };
  },
  computed: {
    isUserAdmin() {
      return this.$userData && this.$userData.cass && this.$userData.cass.isUserAdmin;
    },
    maxPage() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize));
    },
    jwtHeaderPretty() {
      return this.toPrettyJSON(this.detail.jwtHeader);
    },
    jwtPayloadPretty() {
      return this.toPrettyJSON(this.detail.jwtPayload);
    },
  },
  mounted() {
    if (!this.isUserAdmin) {
      return;
    }
    this.setPresetHours(24);
    this.reloadAll();
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleString();
    },
    showSnack(text, color = "success") {
      this.snackbar = { visible: true, text, color };
    },
    toPrettyJSON(value) {
      if (!value || typeof value !== "object") {
        return "{}";
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch (_error) {
        return "{}";
      }
    },
    localInputValue(date) {
      const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return local.toISOString().slice(0, 16);
    },
    setPresetHours(hours) {
      const now = new Date();
      const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
      this.filters.fromLocal = this.localInputValue(from);
      this.filters.toLocal = this.localInputValue(now);
      this.page = 1;
    },
    toUTCISOString(localValue) {
      if (!localValue) {
        return null;
      }
      const parsed = new Date(localValue);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }
      return parsed.toISOString();
    },
    normalizeText(value) {
      const trimmed = (value || "").trim();
      return trimmed || null;
    },
    buildFilter() {
      const filter = {};

      const from = this.toUTCISOString(this.filters.fromLocal);
      const to = this.toUTCISOString(this.filters.toLocal);
      if (from) {
        filter.from = from;
      }
      if (to) {
        filter.to = to;
      }
      if (this.filters.errorCodes && this.filters.errorCodes.length > 0) {
        filter.errorCodes = this.filters.errorCodes;
      }

      const requestId = this.normalizeText(this.filters.requestId);
      const oid = this.normalizeText(this.filters.oid);
      const eligibilityReasonCode = this.normalizeText(this.filters.eligibilityReasonCode);
      const unresolvedReasonCode = this.normalizeText(this.filters.unresolvedReasonCode);

      if (requestId) {
        filter.requestId = requestId;
      }
      if (oid) {
        filter.oid = oid;
      }
      if (eligibilityReasonCode) {
        filter.eligibilityReasonCode = eligibilityReasonCode;
      }
      if (unresolvedReasonCode) {
        filter.unresolvedReasonCode = unresolvedReasonCode;
      }

      const booleanFields = [
        "isEligibleUser",
        "isUserGroupMember",
        "isAdminGroupMember",
        "isBetaGroupMember",
        "isInternalVisibilityMember",
        "hasProjection",
        "isUnresolved",
      ];
      booleanFields.forEach((field) => {
        if (this.filters[field] === true || this.filters[field] === false) {
          filter[field] = this.filters[field];
        }
      });

      return filter;
    },
    async loadSignals() {
      const response = await cass.getAuthFailureSignals();
      this.signals = response.data.items || [];
      this.processStartedAt = response.data.processStartedAt || null;
    },
    async search() {
      this.loading = true;
      try {
        const response = await cass.searchAuthFailures({
          filter: this.buildFilter(),
          page: this.page,
          pageSize: this.pageSize,
          sortBy: this.sortBy,
          sortDirection: this.sortDirection,
        });
        this.items = response.data.items || [];
        this.total = response.data.total || 0;
        this.page = response.data.page || this.page;
        this.pageSize = response.data.pageSize || this.pageSize;
      } catch (error) {
        console.error("Could not search auth failures", error);
        this.showSnack("Could not search auth failures", "error");
      } finally {
        this.loading = false;
      }
    },
    async reloadAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadSignals(), this.search()]);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters.requestId = "";
      this.filters.oid = "";
      this.filters.errorCodes = [];
      this.filters.eligibilityReasonCode = "";
      this.filters.unresolvedReasonCode = "";
      this.filters.isEligibleUser = null;
      this.filters.isUserGroupMember = null;
      this.filters.isAdminGroupMember = null;
      this.filters.isBetaGroupMember = null;
      this.filters.isInternalVisibilityMember = null;
      this.filters.hasProjection = null;
      this.filters.isUnresolved = null;
      this.setPresetHours(24);
      this.sortBy = "at";
      this.sortDirection = "desc";
      this.page = 1;
      this.search();
    },
    pageBack() {
      if (this.page <= 1 || this.loading) {
        return;
      }
      this.page -= 1;
      this.search();
    },
    pageNext() {
      if (this.page >= this.maxPage || this.loading) {
        return;
      }
      this.page += 1;
      this.search();
    },
    async openDetail(item) {
      if (!item || !item.id) {
        return;
      }
      try {
        const response = await cass.getAuthFailure(item.id);
        this.detail = response.data || {};
        this.retention.keepUntilLocal = "";
        this.detailDialog = true;
      } catch (error) {
        console.error("Could not load auth failure detail", error);
        this.showSnack("Could not load auth failure detail", "error");
      }
    },
    openDeleteDialog(item) {
      const id = item && item.id ? item.id : "";
      if (!id) {
        return;
      }
      this.deleteDialog.id = id;
      this.deleteDialog.visible = true;
    },
    async confirmDeleteSingle() {
      if (!this.deleteDialog.id) {
        return;
      }
      this.deleting = true;
      try {
        await cass.deleteAuthFailure(this.deleteDialog.id);
        this.deleteDialog.visible = false;
        if (this.detail.id === this.deleteDialog.id) {
          this.detailDialog = false;
        }
        this.showSnack("Auth failure deleted");
        await this.search();
      } catch (error) {
        console.error("Could not delete auth failure", error);
        this.showSnack("Could not delete auth failure", "error");
      } finally {
        this.deleting = false;
      }
    },
    openBulkDeleteDialog() {
      this.bulkDeleteDialog = true;
    },
    async confirmBulkDelete() {
      this.deleting = true;
      try {
        const response = await cass.bulkDeleteAuthFailures(this.buildFilter());
        const matched = response.data.matchedCount || 0;
        const deleted = response.data.deletedCount || 0;
        this.showSnack(`Bulk delete complete: matched ${matched}, deleted ${deleted}`);
        this.bulkDeleteDialog = false;
        await this.search();
      } catch (error) {
        console.error("Could not bulk delete auth failures", error);
        this.showSnack("Could not bulk delete auth failures", "error");
      } finally {
        this.deleting = false;
      }
    },
    async updateRetention() {
      if (!this.detail.id) {
        return;
      }
      const keepUntil = this.toUTCISOString(this.retention.keepUntilLocal);
      if (!keepUntil) {
        this.showSnack("Please provide a valid keep-until timestamp", "error");
        return;
      }

      this.retention.loading = true;
      try {
        const response = await cass.patchAuthFailureRetention(this.detail.id, keepUntil);
        const ttl = response.data.ttl || 0;
        this.showSnack(`Retention updated (ttl ${ttl}s)`);
      } catch (error) {
        console.error("Could not update retention", error);
        this.showSnack("Could not update retention", "error");
      } finally {
        this.retention.loading = false;
      }
    },
    async copyText(value) {
      const text = value || "";
      try {
        await navigator.clipboard.writeText(text);
        this.showSnack("Copied to clipboard");
      } catch (_error) {
        this.showSnack("Copy failed", "error");
      }
    },
  },
};
</script>

<style scoped>
.v-btn {
  text-transform: none;
}
</style>
